import React from "react"
import { Card, Col, Progress, Row, Statistic, Table, Tag, Tooltip, Typography } from "antd"
import { STAGES, formatDuration } from "./labels"

const percent = (part, total) => (total > 0 ? Math.round((part / total) * 1000) / 10 : 0)

function StageTag({ stage }) {
  const meta = STAGES[stage] ?? { short: stage, color: "default" }
  return <Tag color={meta.color}>{meta.short}</Tag>
}

// Сводка за период: учитывает период и номер, но не способ и статус — чтобы распределение было полным.
export function AuthRequestsSummary({ summary, isLoading }) {
  if (!summary) {
    return <Card size="small" loading={isLoading} style={{ marginBottom: 16 }} />
  }

  const { by_status: byStatus, cost } = summary
  const finished = byStatus.success + byStatus.failed + byStatus.expired
  const totalSuccess = byStatus.success

  return (
    <>
      <Card size="small" loading={isLoading} style={{ marginBottom: 16 }}>
        <Row gutter={[24, 16]}>
          <Col xs={12} md={6} lg={3}>
            <Statistic title="Запросов на авторизацию" value={summary.total} />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Statistic title="Уникальных номеров" value={summary.unique_users} />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Statistic title="Успешно" value={byStatus.success} valueStyle={{ color: "#3f8600" }} />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Tooltip title={`Отказ или повторный запрос — ${byStatus.failed}, тайм-аут — ${byStatus.expired}`}>
              <Statistic title="Неуспешно" value={byStatus.failed + byStatus.expired} valueStyle={{ color: "#cf1322" }} />
            </Tooltip>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Statistic
              title="Конверсия запроса"
              value={percent(byStatus.success, finished)}
              suffix="%"
              precision={1}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Tooltip title="Сколько запросов в среднем понадобилось на один успешный вход (по процессам входа)">
              <Statistic
                title="Запросов на успешный вход"
                value={summary.avg_requests_per_success ?? "—"}
                precision={summary.avg_requests_per_success === null ? undefined : 2}
              />
            </Tooltip>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Tooltip title={`Номеров с повторами — ${summary.users_with_retries}`}>
              <Statistic title="Повторных запросов" value={summary.repeat_requests} />
            </Tooltip>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Tooltip title="Запросов, принятых шлюзом МТС, и отправленных СМС-кодов Буше — для оценки расходов">
              <Statistic title="МТС / СМС Буше" value={`${cost.mts_requests} / ${cost.sms_sent}`} />
            </Tooltip>
          </Col>
        </Row>
        {byStatus.pending > 0 && (
          <Typography.Text type="secondary">В процессе сейчас: {byStatus.pending}</Typography.Text>
        )}
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={10}>
          <Card size="small" title="Запросы по способам" style={{ height: "100%" }}>
            <Table
              size="small"
              rowKey="method"
              pagination={false}
              dataSource={summary.by_method}
              columns={[
                { title: "Способ", dataIndex: "method", render: (method) => <StageTag stage={method} /> },
                { title: "Запросов", dataIndex: "total", align: "right" },
                { title: "Успешно", dataIndex: "success", align: "right" },
                {
                  title: (
                    <Tooltip title="Доля способа среди всех успешных входов за период">Доля входов</Tooltip>
                  ),
                  dataIndex: "success",
                  key: "share",
                  width: 120,
                  render: (value) => <Progress percent={percent(value, totalSuccess)} size="small" />,
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card size="small" title="Этапы каскада" style={{ height: "100%" }}>
            <Table
              size="small"
              rowKey="stage"
              pagination={false}
              dataSource={summary.stages}
              columns={[
                { title: "Этап", dataIndex: "stage", render: (stage) => <StageTag stage={stage} /> },
                { title: "Всего", dataIndex: "total", align: "right" },
                {
                  title: "Успешно",
                  dataIndex: "success",
                  align: "right",
                  render: (value, row) => `${value} (${percent(value, row.total)}%)`,
                },
                { title: "Переход дальше", dataIndex: "next", align: "right" },
                { title: "Ошибка", dataIndex: "error", align: "right" },
                { title: "Тайм-аут", dataIndex: "timeout", align: "right" },
                { title: "Среднее время", dataIndex: "avg_duration_ms", align: "right", render: formatDuration },
              ]}
            />
            {summary.transitions.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Typography.Text strong>Переходы между этапами: </Typography.Text>
                {summary.transitions.map((row) => (
                  <span key={`${row.from}-${row.to}`} style={{ marginRight: 16, whiteSpace: "nowrap" }}>
                    <StageTag stage={row.from} />→ <StageTag stage={row.to} />
                    {row.count}
                  </span>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  )
}
